import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchComments, addComment, deleteComment } from '../services/newsService';
import type { BlogComment } from '../types/news';

const PREVIEW_COUNT = 3;
const MAX_COMMENT_LENGTH = 1000;

interface Props {
  postId: string;
  /** When true: show first 3 comments only, no form, link to full post */
  preview?: boolean;
}

const CommentSection: React.FC<Props> = ({ postId, preview = false }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, total: t } = await fetchComments(postId, preview ? PREVIEW_COUNT : undefined);
    setComments(data);
    setTotal(t);
    setLoading(false);
  }, [postId, preview]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !text.trim()) return;

    setSubmitting(true);
    setSubmitError('');

    const { data: newComment, error } = await addComment(postId, user.id, text.trim());

    if (error || !newComment) {
      setSubmitError(error?.message ?? 'Kunne ikke legge til kommentar.');
      setSubmitting(false);
      return;
    }

    setText('');
    setComments((prev) => [...prev, newComment]);
    setTotal((prev) => prev + 1);
    setSubmitting(false);
  };

  const handleDelete = async (commentId: string) => {
    setDeletingId(commentId);
    const { error } = await deleteComment(commentId);
    if (!error) {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setTotal((prev) => prev - 1);
    }
    setDeletingId(null);
  };

  const hiddenCount = total - comments.length;

  return (
    <div className="mt-6 border-t border-white/10 pt-6">
      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
        {preview ? (
          <Link to={`/blog/${postId}#comments`} className="hover:text-neon-green transition-colors">
            Kommentarer{total > 0 && <span className="ml-2 text-gray-600">({total})</span>}
          </Link>
        ) : (
          <>Kommentarer{total > 0 && <span className="ml-2 text-gray-600">({total})</span>}</>
        )}
      </h3>

      {loading ? (
        <p className="text-xs text-gray-600 animate-pulse">Laster kommentarer...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-600">
          {preview ? 'Ingen kommentarer ennå.' : 'Ingen kommentarer ennå. Bli den første!'}
        </p>
      ) : (
        <ul className="space-y-4">
          {comments.map((comment) => (
            <li key={comment.id} className="flex gap-3 group/comment">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                  {comment.author_display_name ? (
                    <Link
                      to={`/users/${comment.author_display_name}`}
                      className="text-sm font-semibold text-neon-green hover:underline"
                    >
                      {comment.author_display_name}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-gray-500">Ukjent</span>
                  )}
                  <span className="text-xs text-gray-600">{formatDate(comment.created_at)}</span>
                </div>
                <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                  {comment.content}
                </p>
              </div>
              {user?.id === comment.author_id && !preview && (
                <button
                  type="button"
                  onClick={() => handleDelete(comment.id)}
                  disabled={deletingId === comment.id}
                  className="text-xs text-red-500/60 hover:text-red-400 opacity-0 group-hover/comment:opacity-100 transition-opacity disabled:opacity-30 shrink-0 self-start pt-0.5"
                  aria-label="Slett kommentar"
                >
                  {deletingId === comment.id ? '…' : 'Slett'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Preview mode: link to full post if there are more comments or to encourage reading */}
      {preview && (
        <div className="mt-4">
          {hiddenCount > 0 && (
            <Link
              to={`/blog/${postId}#comments`}
              className="text-sm text-neon-green hover:underline"
            >
              Vis {hiddenCount} flere kommentar{hiddenCount !== 1 ? 'er' : ''} →
            </Link>
          )}
          {user ? (
            <Link
              to={`/blog/${postId}#comments`}
              className={`block text-sm text-gray-500 hover:text-gray-300 transition-colors ${hiddenCount > 0 ? 'mt-1' : ''}`}
            >
              Legg til kommentar…
            </Link>
          ) : (
            <Link
              to={`/blog/${postId}#comments`}
              className={`block text-sm text-gray-600 hover:text-gray-400 transition-colors ${hiddenCount > 0 ? 'mt-1' : ''}`}
            >
              Logg inn for å kommentere
            </Link>
          )}
        </div>
      )}

      {/* Full mode: comment form */}
      {!preview && user && (
        <form onSubmit={handleSubmit} className="mt-6">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (text.trim() && !submitting) handleSubmit(e as unknown as React.FormEvent);
              }
            }}
            maxLength={MAX_COMMENT_LENGTH}
            rows={3}
            placeholder="Skriv en kommentar… (Enter for å sende, Shift+Enter for linjeskift)"
            className="input-glow w-full resize-y min-h-[80px] text-sm"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-600">
              {text.length}/{MAX_COMMENT_LENGTH}
            </span>
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              className="btn-primary py-2 px-5 text-sm disabled:opacity-50"
            >
              {submitting ? 'Sender...' : 'Send kommentar'}
            </button>
          </div>
          {submitError && (
            <p className="mt-2 text-xs text-red-400">{submitError}</p>
          )}
        </form>
      )}

      {!preview && !user && (
        <p className="mt-4 text-sm text-gray-600">
          <Link to="/login" className="text-neon-green hover:underline">Logg inn</Link> for å kommentere.
        </p>
      )}
    </div>
  );
};

export default CommentSection;
